import { useCallback, useMemo, useState } from 'react';
import { createContext, useContext } from 'use-context-selector';

import { toastWarning } from '~utils/toast.util';

const initialContextValues = {
  isGettingCurrentLocation: false,
  setIsGettingCurrentLocation: (_value: boolean) => {
    return;
  },
  googleMapKey: 0,
  increaseGoogleMapKey: () => {
    return;
  },
  currentLocation: null as google.maps.LatLngLiteral | null,
  handleGetCurrentLocation: () => {
    return;
  },
};

const GeolocationContext = createContext(initialContextValues);

export const useGeolocationContext = () => {
  return useContext(GeolocationContext);
};

type Props = {
  children: React.ReactNode;
};

export const GeolocationProvider: React.FC<Props> = ({ children }) => {
  const [isGettingCurrentLocation, setIsGettingCurrentLocation] =
    useState<boolean>(false);

  const [googleMapKey, setGoogleMapKey] = useState<number>(0);
  const [currentLocation, setCurrentLocation] =
    useState<google.maps.LatLngLiteral | null>(null);

  const increaseGoogleMapKey = useCallback(() => {
    setGoogleMapKey((prev) => prev + 1);
  }, []);

  const handleGetCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      toastWarning('Geolocation is not supported by your browser.');
      return;
    }

    setIsGettingCurrentLocation(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          lat: position.coords.latitude ?? 0,
          lng: position.coords.longitude ?? 0,
        });
        setIsGettingCurrentLocation(false);
      },
      (error) => {
        // eslint-disable-next-line no-console
        console.error('Error when getting location:', error.message);
        setIsGettingCurrentLocation(false);
      },
    );
  }, []);

  const providerValues = useMemo(
    () => ({
      isGettingCurrentLocation,
      setIsGettingCurrentLocation,
      googleMapKey,
      increaseGoogleMapKey,
      currentLocation,
      handleGetCurrentLocation,
    }),
    [
      currentLocation,
      googleMapKey,
      handleGetCurrentLocation,
      increaseGoogleMapKey,
      isGettingCurrentLocation,
    ],
  );

  return (
    <GeolocationContext.Provider value={providerValues}>
      {children}
    </GeolocationContext.Provider>
  );
};
